"use client";

import { useState, type ReactNode } from "react";
import { CheckCircle } from "lucide-react";

export interface ContentPage {
  id: string | number;
  label: string;
  node: ReactNode;
}

/**
 * Restores the old lesson UX's horizontal "Part 1 / Part 2 / ... / Workplace Scenario"
 * tab bar for prose-based lesson content (Proactive/Professional/legacy shapes), which
 * otherwise render as one continuous scroll with no part navigation. Each page is shown
 * one at a time with Previous/Continue controls; "visited" is purely local UI state (not
 * persisted) since these lessons have no server-tracked per-part progress.
 */
export function PagedContentSections({
  pages,
  lessonCompletedInCourse = false,
}: {
  pages: ContentPage[];
  lessonCompletedInCourse?: boolean;
}) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [visited, setVisited] = useState<Set<number>>(() =>
    lessonCompletedInCourse ? new Set(pages.map((_, i) => i)) : new Set([0]),
  );

  if (pages.length === 0) return null;

  const isAccessible = (index: number) => {
    return lessonCompletedInCourse || visited.has(index) || index <= activeIndex;
  };

  const goTo = (index: number) => {
    if (index < 0 || index >= pages.length) return;
    if (!isAccessible(index)) return;
    setActiveIndex(index);
    setVisited((prev) => (prev.has(index) ? prev : new Set(prev).add(index)));
  };

  const isFirst = activeIndex === 0;
  const isLast = activeIndex === pages.length - 1;
  const current = pages[activeIndex];
  const showNav = pages.length > 1;

  return (
    <div>
      {showNav && (
        <div
          role="tablist"
          aria-label="Lesson parts"
          className="mb-6 flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {pages.map((page, index) => {
            const isCurrent = index === activeIndex;
            const isComplete = visited.has(index) && !isCurrent;
            const canClick = isAccessible(index);
            return (
              <button
                key={page.id}
                type="button"
                role="tab"
                aria-selected={isCurrent}
                aria-current={isCurrent ? "step" : undefined}
                disabled={!canClick}
                onClick={canClick ? () => goTo(index) : undefined}
                className={`flex shrink-0 items-center gap-2 rounded-lg border px-3 py-2.5 text-left text-xs font-medium whitespace-nowrap transition-colors ${
                  canClick ? "cursor-pointer" : "cursor-not-allowed opacity-60"
                } ${
                  isComplete
                    ? "border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
                    : isCurrent
                      ? "border-indigo-300 bg-indigo-50 text-indigo-900"
                      : canClick
                        ? "border-slate-200 bg-slate-50 text-slate-500 hover:bg-slate-100"
                        : "border-slate-200 bg-slate-50 text-slate-400"
                }`}
              >
                {isComplete ? (
                  <CheckCircle className="h-3.5 w-3.5 shrink-0 text-emerald-500" aria-hidden />
                ) : (
                  <span
                    className={`flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold ${
                      isCurrent ? "bg-indigo-600 text-white" : "bg-slate-300 text-slate-600"
                    }`}
                    aria-hidden
                  >
                    {index + 1}
                  </span>
                )}
                <span className="max-w-[10rem] truncate">{page.label}</span>
              </button>
            );
          })}
        </div>
      )}

      <div role="tabpanel">{current.node}</div>

      {showNav && (
        <div className="mt-8 flex items-center justify-between border-t border-slate-200 pt-6">
          <button
            type="button"
            onClick={() => goTo(activeIndex - 1)}
            disabled={isFirst}
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 disabled:cursor-not-allowed disabled:opacity-40 hover:bg-slate-50"
          >
            Previous
          </button>
          {!isLast && (
            <button
              type="button"
              onClick={() => goTo(activeIndex + 1)}
              className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
            >
              Continue
              <span aria-hidden>→</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
